import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Stack,
  Fade,
  MenuList,
  MenuItem,
  Menu,
  useMediaQuery,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import { PartnerBurgerMenu, PartnerMenu } from './PartnerMenu';
import { AuthHeaderButton } from './AuthHeaderButton';
import { IntegrationCenterDropdown } from './IntegrationCenterDropdown';
import { HeaderNavButton } from './HeaderNavButton';
import { AdminDropdown } from './AdminDropdown';
import logo from 'url:../assets/images/logo.svg';

export interface PartnerInfo {
  id: string;
  name: string;
}

export interface HeaderProps {
  checkDevFlag: boolean;
  partnerInfo: PartnerInfo;
  termsAccepted: boolean | undefined;
  workstreams: string[];
  userType: string | null | undefined;
  isPortalAdmin: boolean | null | undefined;
  partnerWorkstreams: any;
  partnerWorkstreamsLength: number | null | undefined;
  handlePartnerClick: (id: string) => void;
  handleClose: () => void;
  handleToggle: () => void;
  handleUserClose: () => void;
  handleUserToggle: () => void;
  handleEventChange: () => void;
  open: boolean;
  openUser: boolean;
  changeNavigation: (path: string) => void;
  switchRole: () => void;
  logout: () => void;
  isAccessManagementAdmin: boolean | null | undefined;
}

const Header = ({
  checkDevFlag,
  partnerInfo,
  termsAccepted,
  workstreams,
  userType,
  isPortalAdmin,
  partnerWorkstreams,
  partnerWorkstreamsLength,
  handlePartnerClick,
  handleClose,
  handleToggle,
  handleUserClose,
  handleUserToggle,
  handleEventChange,
  open,
  openUser,
  changeNavigation,
  switchRole,
  logout,
  isAccessManagementAdmin,
}: HeaderProps) => {
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  const appBarRef = useRef<HTMLDivElement | null>(null);
  const handleCloseMenu = () => {
    setIsNavOpen(false);
  };

  const handleBurgerMenu = (event: React.MouseEvent<HTMLButtonElement>) => setIsNavOpen((prev) => !prev);

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      handleEventChange();
    }
  };

  const isAdminPage = location.pathname.includes('/app/admin');

  const isTiny = useMediaQuery('(max-width:400px)');

  const hideNavigation =
    !termsAccepted ||
    location.pathname.includes('/app/terms-of-use') ||
    location.pathname.includes('/app/full-story-disclosure') ||
    location.pathname.includes('/app/complete-partner-profile');

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname.includes('/app/terms-of-use')) {
      changeNavigation(`/app`);
    }
    if (hideNavigation) {
      return;
    }
    changeNavigation(`/app/partner/${partnerInfo.id}`);
  };

  const hasICWorkstreams =
    workstreams.filter((ws) => ['ADK_Integration', 'Billing_API', 'Customer_Journey', 'Perks'].includes(ws)).length > 0;

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        ref={appBarRef}
        sx={{ display: 'flex', flexDirection: 'row', bgcolor: '#1D2026', color: '#FFFFFF' }}
      >
        <Toolbar sx={(theme) => ({ [theme.breakpoints.down(1025)]: { pr: 0 } })}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleBurgerMenu}
            sx={(theme) => ({
              mr: 2,
              ...(hideNavigation ? { display: 'none' } : { [theme.breakpoints.up(1025)]: { display: 'none' } }),
            })}
          >
            {isNavOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
          <Box>
            <a onClick={handleHomeClick} style={{ padding: '0', color: 'inherit', cursor: 'pointer' }}>
              <img src={logo} alt="The Walt Disney Company Logo" width={isTiny ? '138px' : '203px'} />
            </a>
          </Box>
          {!hideNavigation && (
            <Stack
              direction="row"
              gap="20px"
              sx={(theme) => ({ [theme.breakpoints.down(1025)]: { display: 'none' }, ml: 2 })}
            >
              <HeaderNavButton
                url={`/docs`}
                onClick={changeNavigation}
                title={'Documentation'}
                disabled={!partnerInfo.id}
                isActive={location.pathname.startsWith('/docs')}
              />
              {hasICWorkstreams && (
                <Box
                  style={{
                    flexGrow: 1,
                    textAlign: 'right',
                    alignSelf: 'center',
                  }}
                >
                  <IntegrationCenterDropdown
                    workstreams={workstreams}
                    partnerId={partnerInfo.id}
                    changeNavigation={changeNavigation}
                  />
                </Box>
              )}

              {userType === 'Internal' && (
                <Box style={{ flexGrow: 1, textAlign: 'right', marginRight: '15px', alignSelf: 'center' }}>
                  <AdminDropdown checkDevFlag={checkDevFlag} changeNavigation={changeNavigation} />
                </Box>
              )}
              <HeaderNavButton
                url={`/support/issues`}
                onClick={changeNavigation}
                title={'Support'}
                disabled={!partnerInfo.id}
                isActive={location.pathname.startsWith('/support')}
              />
            </Stack>
          )}
        </Toolbar>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'right',
            alignSelf: 'center',
            alignItems: 'center',
          }}
        >
          {!hideNavigation && !isAdminPage && (
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down('sm')]: {
                  display: 'none',
                },
              })}
            >
              <PartnerMenu
                partnerContextName={partnerInfo.name}
                partnerWorkstreams={partnerWorkstreams}
                partnerWorkstreamsLength={partnerWorkstreamsLength}
                handlePartnerClick={handlePartnerClick}
                handleClose={handleClose}
                handleToggle={handleToggle}
                handleEventChange={handleEventChange}
                open={open}
                changeNavigation={changeNavigation}
              />
            </Box>
          )}
          <Divider orientation="vertical" sx={{ height: '1.5lh', mx: '1.25rem', borderColor: '#F0F2F4' }} />
          <AuthHeaderButton
            userType={userType}
            isPortalAdmin={isPortalAdmin}
            accepted={termsAccepted}
            switchRole={switchRole}
            open={openUser}
            handleUserClose={handleUserClose}
            handleUserToggle={handleUserToggle}
            handleEventChange={handleEventChange}
            changeNavigation={changeNavigation}
            logout={logout}
            isAccessManagementAdmin={isAccessManagementAdmin}
          />
        </Box>
        <Menu
          open={isNavOpen}
          onClose={handleCloseMenu}
          anchorEl={appBarRef.current}
          marginThreshold={0}
          slots={{ transition: Fade }}
          slotProps={{
            paper: {
              sx: { width: '100vw', maxWidth: '100%' },
            },
          }}
        >
          <MenuList id="menu-list-profile" onKeyDown={handleListKeyDown} sx={{ padding: '0' }}>
            <PartnerBurgerMenu
              partnerContextName={partnerInfo.name}
              partnerWorkstreams={partnerWorkstreams}
              partnerWorkstreamsLength={partnerWorkstreamsLength}
              handlePartnerClick={handlePartnerClick}
              handleClose={handleClose}
              handleToggle={handleToggle}
              handleEventChange={handleEventChange}
              open={open}
              changeNavigation={changeNavigation}
            />
            <MenuItem sx={(theme) => ({ [theme.breakpoints.down(1025)]: { display: 'none' } })}></MenuItem>
            <MenuItem sx={{ m: 0 }} onClick={() => changeNavigation('/docs')}>
              Documentation
            </MenuItem>
            <IntegrationCenterDropdown
              workstreams={workstreams}
              partnerId={partnerInfo.id}
              changeNavigation={changeNavigation}
              mobile
            />
            <MenuItem sx={{ m: 0 }} onClick={() => changeNavigation('/support/issues')}>
              Support
            </MenuItem>
          </MenuList>
        </Menu>
      </AppBar>
    </>
  );
};

export default Header;
